/**
 * DecisionsActionsPanel — the on-screen view of the deterministic board
 * decisions + management action tracker that go into the deliverable. Lets the
 * consultant update action *progress* (an overlay that never mutates the frozen
 * report baseline). Reads the frozen snapshot once finalised, a live preview
 * otherwise.
 */
import React, { useCallback, useEffect, useState } from "react";
import { ClipboardList, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const STATUSES = ["Proposed", "In progress", "Blocked", "Done"];
const STATUS_CLS = {
  Proposed: "bg-zinc-100 text-zinc-700",
  "In progress": "bg-amber-50 text-amber-800 border border-amber-200",
  Blocked: "bg-red-50 text-red-700 border border-red-200",
  Done: "bg-emerald-50 text-emerald-700 border border-emerald-200",
};

export default function DecisionsActionsPanel({ runId, refreshKey }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/decisions`);
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load decisions");
    } finally { setLoading(false); }
  }, [runId]);

  useEffect(() => { if (open) load(); }, [open, load, refreshKey]);

  const setStatus = async (action, status) => {
    try {
      await api.put(`/admin/assessments/runs/${runId}/actions/${action.id}`, { status, note: action.progress_note || null });
      setData((d) => ({
        ...d,
        actions: d.actions.map((a) => (a.id === action.id ? { ...a, progress_status: status } : a)),
      }));
      toast.success("Action progress updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update action");
    }
  };

  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="decisions-actions-panel">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2" data-testid="decisions-toggle">
        <span className="flex items-center gap-2 text-zinc-700 text-[12px] font-medium uppercase tracking-[0.16em]"><ClipboardList size={14}/> Decisions &amp; actions</span>
        {open ? <ChevronUp size={16} className="text-zinc-400"/> : <ChevronDown size={16} className="text-zinc-400"/>}
      </button>

      {open && (
        <div className="mt-4 space-y-4" data-testid="decisions-body">
          {loading && <div className="text-[12px] text-zinc-500">Loading…</div>}
          {data && (
            <>
              <div className="text-[11px] text-zinc-500">
                {data.frozen
                  ? <span className="text-[#7737F6] font-medium">Frozen baseline · v{data.frozen_version}</span>
                  : <span>Live preview — finalise to freeze this baseline</span>}
              </div>

              <div className="space-y-2" data-testid="decisions-list">
                {(data.decisions || []).map((d) => (
                  <div key={d.n} className="border border-zinc-200 bg-[#FAF7FF] p-3" data-testid={`decision-${d.n}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#7737F6]">Decision {String(d.n).padStart(2, "0")}</span>
                      <span className="text-[10.5px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5">{d.recommendation}</span>
                    </div>
                    <div className="text-[13px] font-medium text-zinc-900 mt-1.5">{d.title}</div>
                    <div className="text-[12px] text-zinc-600 mt-1 leading-relaxed">{d.why}</div>
                    <div className="text-[11px] text-zinc-500 mt-2 flex flex-wrap gap-x-4 gap-y-1">
                      <span><span className="text-zinc-400">Owner:</span> {d.owner}</span>
                      <span><span className="text-zinc-400">By:</span> {d.decide_by}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div data-testid="actions-list">
                <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-2">Management actions — progress</div>
                <div className="space-y-2">
                  {(data.actions || []).map((a) => (
                    <div key={a.id || a.n} className="border border-zinc-200 p-2.5" data-testid={`action-${a.id || a.n}`}>
                      <div className="text-[12.5px] font-medium text-zinc-800">{a.action}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">{a.owner} · {a.due}</div>
                      <div className="mt-2 flex items-center gap-2">
                        <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 ${STATUS_CLS[a.progress_status] || STATUS_CLS.Proposed}`}>{a.progress_status}</span>
                        <select
                          value={a.progress_status || "Proposed"}
                          onChange={(e) => setStatus(a, e.target.value)}
                          className="text-[12px] border border-zinc-300 px-1.5 py-1 bg-white ml-auto"
                          data-testid={`action-status-${a.id || a.n}`}
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[10.5px] text-zinc-400 mt-2">Progress is an overlay — it never changes the finalised report baseline.</p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
